import React, {useState, useReducer, useEffect} from "react";
import { useParams, useHistory } from "react-router-dom";
import Heading from "@amzn/meridian/heading";
import { Translation, useTranslation } from "./Translation";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Checkbox from "@amzn/meridian/checkbox"
import { ScrubTable } from "./ScrubTable";
import Pagination from "@amzn/meridian/pagination";
import Breadcrumb, { BreadcrumbGroup } from "@amzn/meridian/breadcrumb";
import {
    SCRUB_VIEW_TYPE,
    SCRUB_LIST_HEADERS,
    SCRUB_PAGE_SIZE,
    SCRUB_LIST_IDENTIFIER,
    SCRUB_CSV_DATE_FORMAT,
    MISSING_VIEW_NEW_STRATEGY_FEATURE_NAME,
    SELECTIVE_MARKING_ENABLED,
    LOST_QUEUE_SLA,
    REMOVE_MISSING_DELIVERED_SCAN_ENABLED, TERMINAL_SCAN_AGEING_MISSING_QUEUE_ENABLED, MISSING_QUEUE_SLA
} from "../../commons/constants/ScrubConstants";
import { ScrubListReducer } from "../../commons/reducer/ScrubListReducer";
import Loader from "@amzn/meridian/loader";
import Text from "@amzn/meridian/text";
import { ScrubUpdateButton } from "./ScrubUpdateButton";
import { exportToCsv } from "../../handler/CsvHandler";
import {getFormattedDate, withRefreshTime} from "../../util/CommonUtils";
import Column from "@amzn/meridian/column";
import Alert from "@amzn/meridian/alert";
import {getRegion} from "../../util/CommonUtils";
import {REGION} from "../../commons/constants/CommonConstants";
import RefreshTimer from "../../commons/RefreshTimer";
import {ExceptionsClient} from "../../commons/client/ExceptionsClient";
import {getDocumentURL, getTcpRolesFromParent} from "../../handler/ParentDataHandler";

const initialState = {
    items: [],
    loading: false
};

export const ScrubListView = () => {
    const {view} = useParams();
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const [{items, loading, selectedItems}, dispatch] = useReducer(ScrubListReducer, initialState);
    const translatedHeaders = SCRUB_LIST_HEADERS[view]
    .map(([k, v]) => useTranslation({stringId:v.stringId}));
    const totalHits = (items && items.length) ? items[0].TOTAL_HITS: 0;
    const numberOfTimes = Math.ceil(totalHits / 5000) - 1;
    const [missingViewNewStrategyEnabled, setMissingViewNewStrategyEnabled] = useState(false);
    const [selectiveMarkingEnabled, setSelectiveMarkingEnabled] = useState(false);
    const [removeMissingDeliverScanEnabled, setRemoveMissingDeliverScanEnabled] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [baseURL, setBaseURL] = useState("");
    const [lostQueueSLA, setLostQueueSLA] = useState("7");
    const [missingQueueSLA, setMissingQueueSLA] = useState("7");
    const [missingQueueSLAEnabled, setMissingQueueSLAEnabled] = useState(false);

    useEffect(() => {
        dispatch({
            type : "SET_LOADING",
            data : {loadingValue : true}
        })

        ExceptionsClient.getFeatureStatus({
            featureName : LOST_QUEUE_SLA
        }).then((response) => {
            setLostQueueSLA(response.data.featureMetaData.AUTOMATIC_SELECTION_SLA_FOR_LOST_QUEUE);
        });

        ExceptionsClient.getFeatureStatus({
            featureName : MISSING_QUEUE_SLA
        }).then((response) => {
            setMissingQueueSLA(response.data.featureMetaData.TERMINAL_SCAN_REQUEST_AGEING_SLA_FOR_MISSING_QUEUE);
        });

        ExceptionsClient.getFeatureStatus({
            featureName : SELECTIVE_MARKING_ENABLED
        }).then(({valid, data}) => {
            setSelectiveMarkingEnabled(valid && data.status);
        });

        ExceptionsClient.getFeatureStatus({
            featureName : REMOVE_MISSING_DELIVERED_SCAN_ENABLED
        }).then(({valid, data}) => {
            setRemoveMissingDeliverScanEnabled(valid && data.status);
        });

        ExceptionsClient.getFeatureStatus({
            featureName : TERMINAL_SCAN_AGEING_MISSING_QUEUE_ENABLED
        }).then(({valid, data}) => {
            setMissingQueueSLAEnabled(valid && data.status);
        });

        ExceptionsClient.getFeatureStatus({
            featureName : MISSING_VIEW_NEW_STRATEGY_FEATURE_NAME
        }).then(({valid, data}) => {
            setMissingViewNewStrategyEnabled(valid && data.status);
            const repeat = withRefreshTime(() =>  dispatch({
                type : "LOAD_DATA",
                data: { targetUseCase:SCRUB_VIEW_TYPE[view].targetUseCase, missingViewNewStrategyEnabled: valid && data.status, dispatcher: dispatch }
            }), 300000);
            return () => { if(repeat) { clearInterval(repeat)}}
        });

        getDocumentURL().then(result => {
            setBaseURL(result);
        })

    }, [view]);

    useEffect(() => {
        (async () => {
            const tcpRolesResponse = await getTcpRolesFromParent();
            if(tcpRolesResponse.roles.includes("Admin")) {
                setIsAuthorized(true);
            } else {
                setIsAuthorized(true);
            }
        })()
    }, []);

    const [showMNR, setShowMNR] = useState(false);

    const nonMNRPackagesCount = () => {
        return getNonMNRPackages(items, missingViewNewStrategyEnabled)
            .filter(item => item.IS_TERMINAL_SCAN !== true).length;
    };

    const getTerminalPackageCount = () => {
        return getTerminalPackages(items).length;
    }

    const itemsList = () => {
        return view === 'scrub_missing' ?
            (showMNR ? items : getNonMNRPackages(items, missingViewNewStrategyEnabled)) : items;
    };
    const pageCount = Math.ceil(itemsList().length/SCRUB_PAGE_SIZE);

    if (loading) {
        return (<Row alignmentHorizontal="center" alignmentVertical="center" height="100%" minHeight="460px" >
            <Loader type="circular" />
            </Row>)
    }

    return (
    <React.Fragment>
    <Row spacingInset="large" width="100%" widths="fill" >
        <Column >
            {numberOfTimes >= 1 &&
            <Row alignmentHorizontal="left" alignmentVertical="center">
                <Alert type="warning" size="medium"><Translation stringId="scrub_high_packages_warning"
                        placeholder={[["X", numberOfTimes]]} />
                </Alert>
            </Row>
            }
        <Box>
            <Heading level={3}>
                <Translation stringId={SCRUB_VIEW_TYPE[view].tableHeader} />
            </Heading> 
        </Box><Box>
            <BreadcrumbGroup>
                <Breadcrumb onClick={() => history.push("/ageingBoard")} >
                    <Translation stringId="scrub_heading" />
                </Breadcrumb>
                <Breadcrumb>
                    <Translation stringId={SCRUB_VIEW_TYPE[view].title} />
                </Breadcrumb>
            </BreadcrumbGroup>
            </Box>
            <RefreshTimer deps={[items]}/>
            {view === 'scrub_missing' ?
                <Box>
                    <Checkbox checked={showMNR} onChange={setShowMNR}>
                        <Translation stringId={SCRUB_VIEW_TYPE[view].show_mnr} />
                    </Checkbox>
                </Box> : null
            }
        </Column>
        <Box >
            <Row spacing="medium" alignmentHorizontal="right" >
            <Button onClick={() => exportToCsv({
                title: `${SCRUB_VIEW_TYPE[view].targetUseCase}-${getFormattedDate(SCRUB_CSV_DATE_FORMAT)}`,
                headers: translatedHeaders,
                rows: itemsList().map(item => SCRUB_LIST_HEADERS[view].map(([k, v]) => item[k] || ""))
            })} type="tertiary" size="medium">
                <Translation stringId="export_to_csv" />
            </Button>
            <ScrubUpdateButton view={view} count={items.length - getTerminalPackageCount()}
                               nonMNRCount={nonMNRPackagesCount()}
                               allItems={items}
                               selectiveMarkingEnabled={selectiveMarkingEnabled}
                               selectedItems={selectedItems} lostDaysConfiguration={lostQueueSLA}
                               isAuthorized={isAuthorized}
                               baseURL={baseURL}
                               removeMissingDeliverScanEnabled={removeMissingDeliverScanEnabled}
                               missingQueueSLA={missingQueueSLA}
                               missingQueueSLAEnabled={missingQueueSLAEnabled}/>
            </Row>
        </Box>
    </Row>
    <Column spacingInset="small large" >
    <Box>
    <ScrubTable dispatch={dispatch} selectiveMarkingEnabled={selectiveMarkingEnabled} headers={SCRUB_LIST_HEADERS[view]} contents=
    {itemsList().slice( (currentPage - 1)*SCRUB_PAGE_SIZE, currentPage*SCRUB_PAGE_SIZE )}
    identifier={SCRUB_LIST_IDENTIFIER} />
    </Box>
    <Row width="100%" widths="fill" >
    <Box>
        <Text type="b200" >
        <Translation stringId="scrub_pagination_entries" 
        placeholder={ [["X", Math.min((currentPage - 1)*SCRUB_PAGE_SIZE + 1 , itemsList().length)],
        ["Y", Math.min((currentPage - 1)*SCRUB_PAGE_SIZE + 50, itemsList().length) ],
        ["Z", itemsList().length ]] } />
        </Text>
    </Box>
    <Row alignmentHorizontal="right" >
    { (pageCount > 1) && <Pagination
        numberOfPages={pageCount}    
        onChange={setCurrentPage}
        currentPage={currentPage}
         />
    }
    </Row>
    </Row>
    </Column>
    </React.Fragment>
    );
}

function getNonMNRPackages(packages, missingViewNewStrategyEnabled) {
    if(missingViewNewStrategyEnabled) {
        return packages.filter(pkg => pkg.IS_MNR === false);
    } else {
        if (REGION.IN === getRegion()) {
            return packages.filter(pkg => pkg.STATUS !== "MANIFESTED" && pkg.STATUS !== "BETWEEN_FC_AND_STATIONS");
        } else {
            return packages.filter(pkg => !((pkg.STATUS === "MANIFESTED" || pkg.STATUS === "BETWEEN_FC_AND_STATIONS") && !pkg.INDUCTED_CYCLE));
        }
    }
}

function getTerminalPackages(items) {
    return items.filter(item => item.IS_TERMINAL_SCAN);
}


// WEBPACK FOOTER //
// ./src/components/scrub/ScrubListView.js